import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FeatureLink } from '../../../components/FeatureLink';
import { useFeatureNav } from '../../../hooks/useFeatureNav';
import {
  Sparkles, ArrowRight, Wrench, Check, Circle, ChevronDown, Plus, Loader2, CheckCircle2, FlaskConical, RotateCcw, Share2, Clock, Copy, Trash2, Lock, Play, Building2, CalendarClock, X, SlidersHorizontal,
} from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import ScheduleSkillDialog from '../../../components/settings/ScheduleSkillDialog';
import type { MyToolsData, DiveTrackerData, ToolDive, PersonalSkill, VettingData, Graduation, ToolDelegation, DelegationAction, ToolControl } from '../../../types/aai';
import { useAsync } from '../useAsync';
import { Loading, ErrorNote } from '../components';
import { Card, MonoLabel, PageHeader } from '../dashboards/ui';
import RunSkillModal from '../RunSkillModal';
import TryToolModal from './TryToolModal';

// A small glyph square (first letter) standing in for a tool icon — keeps the
// design's tool-card shape without inventing per-tool brand art.
const TOOL_TONES = [
  'bg-brand-50 text-brand-600',
  'bg-emerald-50 text-emerald-600',
  'bg-accent-100 text-amber-700',
  'bg-purple-50 text-purple-600',
];

// ── one shared read of "where is this tool at" ─────────────────────────────
// Every row used to be a collapsed accordion that revealed its own state only
// once opened, so the page read as a list of identical grey rows. Connection +
// delegation state is fetched once per tool here and shared by every zone, so
// a tool's status is visible before you touch it.
type ToolStatus = {
  available: boolean;   // a connector exists for this name at all
  connected: boolean;
  decided: number;
  total: number;
  loaded: boolean;
};

const BLANK_STATUS: ToolStatus = { available: false, connected: false, decided: 0, total: 0, loaded: false };

const ToolStatusCtx = React.createContext<{
  status: (tool: string) => ToolStatus;
  track: (tool: string) => void;
  refresh: (tool: string) => void;
}>({ status: () => BLANK_STATUS, track: () => undefined, refresh: () => undefined });

const ToolStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [map, setMap] = useState<Record<string, ToolStatus>>({});
  // Ref-held so a re-render can't re-trigger in-flight fetches (the same
  // effect-loop shape that flooded the intake endpoint once already).
  const inflight = useRef<Set<string>>(new Set());

  const fetchOne = useCallback(async (tool: string): Promise<void> => {
    const key = tool.toLowerCase();
    if (inflight.current.has(key)) return;
    inflight.current.add(key);
    const next: ToolStatus = { ...BLANK_STATUS, loaded: true };
    try {
      const { data } = await aaiApi.getMyConnection(tool);
      next.available = !!data.available;
      next.connected = !!data.connected;
    } catch { /* not connectable here — the tile says so */ }
    try {
      const { data } = await aaiApi.getDelegation(tool);
      next.decided = data.progress.decided;
      next.total = data.progress.total;
    } catch { /* delegation is best-effort */ }
    setMap((prev) => ({ ...prev, [key]: next }));
  }, []);

  const refresh = useCallback((tool: string) => {
    inflight.current.delete(tool.toLowerCase());
    void fetchOne(tool);
  }, [fetchOne]);

  const status = useCallback(
    (tool: string) => map[tool.toLowerCase()] || BLANK_STATUS,
    [map],
  );
  return (
    <ToolStatusCtx.Provider value={{ status, track: fetchOne, refresh }}>{children}</ToolStatusCtx.Provider>
  );
};

// Consumers register themselves, so a dive on a tool that never appeared in the
// onboarding stack still gets its status read (it used to sit on "checking…").
// fetchOne is ref-guarded, so registering twice costs nothing.
const useToolStatus = (tool: string): ToolStatus => {
  const { status, track } = useContext(ToolStatusCtx);
  useEffect(() => { if (tool.trim()) track(tool); }, [tool, track]);
  return status(tool);
};

// A numbered zone header — the page follows the program's own order (connect
// your tools, dive one a week, keep what worked as a skill) and now says so.
const Zone: React.FC<{
  step: number; label: string; blurb?: string; aside?: React.ReactNode; children: React.ReactNode;
}> = ({ step, label, blurb, aside, children }) => (
  <Card>
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="flex items-start gap-2.5 min-w-0">
        <span className="w-5 h-5 mt-px rounded-full bg-brand-500 text-white grid place-items-center text-[11px] font-bold shrink-0">
          {step}
        </span>
        <div className="min-w-0">
          <MonoLabel>{label}</MonoLabel>
          {blurb && <p className="text-[12.5px] text-ink-dim mt-1 leading-snug">{blurb}</p>}
        </div>
      </div>
      {aside && <div className="shrink-0 text-right">{aside}</div>}
    </div>
    {children}
  </Card>
);

// The 4-step dive format as dots — readable at a glance, no opening required.
const StepDots: React.FC<{ steps: { done: boolean }[] }> = ({ steps }) => (
  <span className="inline-flex items-center gap-1">
    {steps.map((s, i) => (
      <span key={i} className={`h-1.5 w-1.5 rounded-full ${s.done ? 'bg-emerald-500' : 'bg-line'}`} />
    ))}
  </span>
);

// What a tool's status says in words. One place, so a tile and a dive row can
// never disagree about the same tool.
const statusLine = (st: ToolStatus): { text: string; tone: string } => {
  if (!st.loaded) return { text: 'checking…', tone: 'text-ink-soft' };
  if (!st.available) return { text: 'no connector for this yet', tone: 'text-ink-soft' };
  if (!st.connected) return { text: 'not connected yet', tone: 'text-ink-soft' };
  if (st.total > 0 && st.decided >= st.total) return { text: `connected · ${st.decided} rules set`, tone: 'text-emerald-600' };
  if (st.total > 0) return { text: `connected · ${st.total - st.decided} rules to set`, tone: 'text-amber-600' };
  return { text: 'connected', tone: 'text-emerald-600' };
};

// ── Zone 1: a real tile per tool, connection state on its face ─────────────
// The old "Tools you use daily" grid was a letter square and a name, which told
// the participant nothing they didn't already know. A tool's tile now carries
// the only thing they need from this page: is it wired to my assistant, and
// have I said what it may do.
const ToolTile: React.FC<{ label: string; tone: string; onOpen: (tool: string) => void }> = ({ label, tone, onOpen }) => {
  const st = useToolStatus(label);
  const { state, error, connect } = useConnectTool(label, () => undefined, { skipInitialCheck: true });
  const line = statusLine(st);
  const connectable = st.loaded && st.available && !st.connected;

  return (
    <div className="bg-white rounded-lg border border-line-soft px-3.5 py-3 flex flex-col gap-2">
      <div className="flex items-center gap-2.5 min-w-0">
        <span className={`w-8 h-8 rounded-lg grid place-items-center font-sans font-bold text-[13px] shrink-0 ${tone}`}>
          {(label || '?').charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold text-ink truncate">{label}</span>
          <span className={`block text-[11.5px] ${line.tone}`}>{line.text}</span>
        </span>
      </div>
      {st.connected && (
        <button onClick={() => onOpen(label)}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-line text-ink-muted hover:border-brand-200 hover:text-brand-700 text-[11.5px] font-semibold py-1.5">
          <SlidersHorizontal size={11} /> What it may do
        </button>
      )}
      {connectable && (
        <button onClick={connect} disabled={state === 'connecting'}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[11.5px] font-semibold py-1.5">
          {state === 'connecting' && <Loader2 size={11} className="animate-spin" />} Connect {label}
        </button>
      )}
      {error && <p className="text-[11px] text-ink-soft">{error}</p>}
    </div>
  );
};

const ToolTiles: React.FC<{ tools: { value: string; label: string }[] }> = ({ tools }) => {
  const [controlling, setControlling] = useState<string | null>(null);
  if (tools.length === 0) {
    return (
      <p className="text-[13px] text-ink-dim bg-white rounded-lg border border-line-soft px-3.5 py-3">
        No tools captured yet. Your onboarding answers fill this in, or add one in the dive queue below.
      </p>
    );
  }
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {tools.map((t, i) => (
          <ToolTile key={t.value || t.label || i} label={t.label} tone={TOOL_TONES[i % TOOL_TONES.length]}
            onOpen={setControlling} />
        ))}
      </div>
      {controlling && <ToolControlModal tool={controlling} onClose={() => setControlling(null)} />}
    </>
  );
};

// ── TD-1/2/3/4: the delegation walk — the heart of the dive ────────────────
// Would you let an assistant do this for you? One capability at a time:
// yes / only with approval / no. Risk tiers are OUR classification; actions IT
// switched off arrive greyed and labeled as policy, never quietly missing.
const RISK_PILL: Record<string, { label: string; cls: string }> = {
  read: { label: 'reads', cls: 'bg-canvas-inset text-ink-muted' },
  draft: { label: 'drafts', cls: 'bg-brand-50 text-brand-700' },
  act: { label: 'acts as you', cls: 'bg-amber-50 text-amber-700' },
  destructive: { label: 'deletes things', cls: 'bg-red-50 text-red-600' },
};
const DECISION_LABEL: Record<string, string> = { yes: 'Yes', approval: 'With approval', no: 'No' };
const DECISION_CLS: Record<string, string> = {
  yes: 'text-emerald-600', approval: 'text-amber-600', no: 'text-red-500',
};

const DelegationWalk: React.FC<{ tool: string }> = ({ tool }) => {
  const [data, setData] = useState<ToolDelegation | null>(null);
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await aaiApi.getDelegation(tool); setData(data); }
      catch { /* the checklist still works without the walk */ }
    })();
  }, [tool]);

  if (!data) return null;
  const decide = async (a: DelegationAction, decision: 'yes' | 'approval' | 'no') => {
    setBusy(true);
    try { const { data: next } = await aaiApi.recordDelegation(tool, a.key, decision); setData(next); setChanging(null); }
    catch { /* best-effort */ }
    finally { setBusy(false); }
  };

  const blocked = data.actions.filter((a) => a.blocked);
  const undecided = data.actions.filter((a) => !a.blocked && !a.decision);
  const decided = data.actions.filter((a) => !a.blocked && a.decision);
  const current = undecided[0];

  const DecisionButtons: React.FC<{ a: DelegationAction }> = ({ a }) => (
    <div className="flex flex-wrap gap-1.5">
      <button onClick={() => decide(a, 'yes')} disabled={busy}
        className="rounded-full border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-[12px] font-semibold px-3 py-1 disabled:opacity-50">Yes</button>
      <button onClick={() => decide(a, 'approval')} disabled={busy}
        className="rounded-full border border-amber-300 text-amber-700 hover:bg-amber-50 text-[12px] font-semibold px-3 py-1 disabled:opacity-50">Only with approval</button>
      <button onClick={() => decide(a, 'no')} disabled={busy}
        className="rounded-full border border-red-300 text-red-600 hover:bg-red-50 text-[12px] font-semibold px-3 py-1 disabled:opacity-50">No</button>
    </div>
  );

  return (
    <div className="rounded-lg border border-brand-100 bg-brand-50/40 px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-semibold text-ink">Delegation — what may your assistant do here?</span>
        <span className="text-[11px] text-ink-dim shrink-0">{data.progress.decided}/{data.progress.total} decided</span>
      </div>
      {data.total_available > data.actions.length && (
        <p className="text-[11px] text-ink-dim">
          This tool exposes {data.total_available} actions — here are the ones that matter for you.
        </p>
      )}
      {current ? (
        <div className="bg-white rounded-lg border border-line px-3 py-2.5">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-[13px] font-semibold text-ink">{current.label}</span>
            <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${RISK_PILL[current.risk]?.cls || ''}`}>{RISK_PILL[current.risk]?.label || current.risk}</span>
          </div>
          {current.touches && <p className="text-[11.5px] text-ink-dim mb-2">Touches: {current.touches}</p>}
          <p className="text-[11.5px] text-ink-dim mb-2">Would you let an assistant do this for you?</p>
          <DecisionButtons a={current} />
        </div>
      ) : data.progress.total > 0 ? (
        <p className="text-[12px] text-emerald-700">All decided — every skill built for you now inherits these answers.</p>
      ) : null}
      {decided.length > 0 && (
        <div className="space-y-1">
          {decided.map((a) => (
            <div key={a.key} className="flex items-center gap-2 text-[11.5px]">
              <span className={`font-semibold w-24 shrink-0 ${DECISION_CLS[a.decision!]}`}>{DECISION_LABEL[a.decision!]}</span>
              <span className="text-ink-muted min-w-0 truncate flex-1">{a.label}</span>
              {changing === a.key ? (
                <DecisionButtons a={a} />
              ) : (
                <button onClick={() => setChanging(a.key)} className="text-ink-soft hover:text-brand-600 shrink-0">change</button>
              )}
            </div>
          ))}
        </div>
      )}
      {blocked.length > 0 && (
        <div className="space-y-0.5">
          {blocked.map((a) => (
            <p key={a.key} className="text-[11.5px] text-ink-soft flex items-center gap-1.5">
              <Lock size={11} className="shrink-0" />
              <span className="line-through">{a.label}</span>
              <span className="shrink-0">· {a.blocked}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

/*
 * The tool control: one connected tool, opened from its tile.
 *
 * Three questions the call asked about a tool, and they are one panel rather
 * than three screens: what can it do, which of those do I allow, and what
 * breaks if I take an allowance away. Splitting them is how somebody switches
 * something off and finds out the cost afterwards.
 *
 * This is NOT the delegation walk above. The walk asks one undecided thing at a
 * time, on the way through a dive. This lists everything at once and lets any
 * answer be changed, which is what a control is. Both write the same rows.
 */
const RISK_GROUPS: Array<{ risk: string; title: string; note: string }> = [
  { risk: 'read', title: 'Reads', note: 'Looks at things. Changes nothing.' },
  { risk: 'draft', title: 'Drafts', note: 'Prepares something for you to check before it goes.' },
  { risk: 'act', title: 'Acts as you', note: 'Does things in your name. Always stops for your confirmation first.' },
  { risk: 'destructive', title: 'Deletes things', note: 'Never offered to an assistant, whatever anyone answers.' },
];

/* What this tool may do for you, and the switches that change it. Exported
   because the catalogue on the Settings tools screen is where a person meets
   their tools now, and the permissions have to be editable from there. */
export const ToolControlModal: React.FC<{ tool: string; onClose: () => void }> = ({ tool, onClose }) => {
  const [data, setData] = useState<ToolControl | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: next } = await aaiApi.getToolControl(tool);
      setData(next);
    } catch {
      setFailed(true);
    }
  }, [tool]);

  useEffect(() => { void load(); }, [load]);

  const decide = async (a: DelegationAction, decision: 'yes' | 'approval' | 'no') => {
    setBusy(true);
    try {
      await aaiApi.recordDelegation(tool, a.key, decision);
      await load();
    } catch { /* best effort: the list re-reads either way */ }
    finally { setBusy(false); }
  };

  const switchable = data?.switchable_risks || [];

  return (
    <div className="fixed inset-0 z-[200] bg-black/30 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[86vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-line-soft">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">What this tool may do for you</div>
            <h3 className="m-0 text-[17px] font-bold text-ink capitalize">{tool}</h3>
          </div>
          <button onClick={onClose} className="text-ink-soft hover:text-ink shrink-0" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {failed && (
          <p className="px-5 py-4 m-0 text-[13px] text-ink-dim">
            This tool&apos;s permissions could not be read just now. Nothing has changed.
          </p>
        )}
        {!data && !failed && <p className="px-5 py-4 m-0 text-[13px] text-ink-soft">Reading what this tool can do…</p>}

        {data && (
          <>
            <div className="px-5 py-3 flex flex-wrap items-center gap-3 text-[12px] border-b border-line-soft">
              <span className="text-emerald-600 font-semibold">{data.summary.allowed} allowed</span>
              <span className="text-red-500 font-semibold">{data.summary.refused} refused</span>
              <span className="text-ink-dim">{data.summary.undecided} not decided</span>
              {data.total_available > data.actions.length && (
                <span className="text-ink-soft">of {data.total_available} this tool exposes, these are the ones that matter for you</span>
              )}
            </div>

            {RISK_GROUPS.map((g) => {
              const rows = data.actions.filter((a) => a.risk === g.risk);
              if (rows.length === 0) return null;
              const canSwitch = switchable.includes(g.risk);
              return (
                <div key={g.risk} className="px-5 py-3 border-b border-line-soft">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${RISK_PILL[g.risk]?.cls || ''}`}>{g.title}</span>
                    <span className="text-[11.5px] text-ink-dim">{g.note}</span>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {rows.map((a) => (
                      <div key={a.key} className="flex flex-wrap items-center gap-2 text-[12.5px]">
                        {a.blocked && <Lock size={12} className="text-ink-soft shrink-0" />}
                        <span className={`min-w-0 flex-1 ${a.blocked ? 'text-ink-soft line-through' : 'text-ink'}`}>{a.label}</span>
                        {a.blocked && <span className="text-[11.5px] text-ink-soft shrink-0">{a.blocked}</span>}
                        {!a.blocked && !canSwitch && (
                          <span className="text-[11.5px] text-ink-soft shrink-0">never offered</span>
                        )}
                        {!a.blocked && canSwitch && (
                          <span className="flex items-center gap-1.5 shrink-0">
                            {a.decision && (
                              <span className={`font-semibold ${DECISION_CLS[a.decision]}`}>{DECISION_LABEL[a.decision]}</span>
                            )}
                            <button onClick={() => decide(a, 'yes')} disabled={busy}
                              className="rounded-full border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-[11px] font-semibold px-2 py-0.5 disabled:opacity-50">Yes</button>
                            <button onClick={() => decide(a, 'approval')} disabled={busy}
                              className="rounded-full border border-amber-300 text-amber-700 hover:bg-amber-50 text-[11px] font-semibold px-2 py-0.5 disabled:opacity-50">Ask me</button>
                            <button onClick={() => decide(a, 'no')} disabled={busy}
                              className="rounded-full border border-red-300 text-red-600 hover:bg-red-50 text-[11px] font-semibold px-2 py-0.5 disabled:opacity-50">No</button>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {(data.repeats || []).length > 0 && (
              <div className="px-5 py-4 border-b border-line-soft">
                <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft mb-1.5">
                  You keep doing this
                </div>
                <p className="m-0 mb-2 text-[12.5px] text-ink-dim">
                  Saving it as a skill means doing it once more, not every time.
                </p>
                <div className="space-y-1.5">
                  {(data.repeats || []).map((r) => (
                    <div key={r.action_key} className="flex flex-wrap items-center gap-2 text-[12.5px]">
                      <span className="min-w-0 flex-1 text-ink">{r.label}</span>
                      <span className="shrink-0 text-[11.5px] text-ink-soft">{r.times} times</span>
                      <FeatureLink
                        to={`/skill-builder?name=${encodeURIComponent(r.suggested_name)}&problem=${encodeURIComponent(r.suggested_problem)}`}
                        className="shrink-0 rounded-full border border-brand-200 px-2 py-0.5 text-[11px] font-semibold text-brand-700 hover:bg-brand-50">
                        Save as a skill
                      </FeatureLink>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="px-5 py-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft mb-1.5">
                What uses this
              </div>
              {data.skills.length === 0 && (
                <p className="m-0 text-[12.5px] text-ink-dim">
                  No skill has said it needs {tool}. That is not proof nothing uses it: a skill
                  built before skills recorded their tools says nothing either way.
                </p>
              )}
              {data.skills.length > 0 && (
                <>
                  <p className="m-0 mb-2 text-[12.5px] text-ink-dim">
                    Turning something off here affects these, so you can see the cost before you decide.
                  </p>
                  <div className="space-y-1">
                    {data.skills.map((sk) => (
                      <div key={sk.id} className="flex items-center gap-2 text-[12.5px]">
                        <span className="min-w-0 flex-1 truncate text-ink">{sk.name}</span>
                        {!sk.published && <span className="text-[11px] text-ink-soft shrink-0">draft</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// The member-scoped OAuth flow, shared by the dive's "set it up" row and the
// tool tiles at the top of the page — one popup implementation, one place that
// tells the shared status map to re-read.
type ConnectState = 'checking' | 'unavailable' | 'connected' | 'idle' | 'connecting';

const useConnectTool = (tool: string, onConnected: () => void, opts?: { skipInitialCheck?: boolean }) => {
  const [state, setState] = useState<ConnectState>(opts?.skipInitialCheck ? 'idle' : 'checking');
  const [error, setError] = useState<string | null>(null);
  const { refresh } = useContext(ToolStatusCtx);

  const check = useCallback(async () => {
    try {
      const { data } = await aaiApi.getMyConnection(tool);
      if (!data.available) { setState('unavailable'); return false; }
      setState(data.connected ? 'connected' : 'idle');
      return data.connected;
    } catch { setState('unavailable'); return false; }
  }, [tool]);

  // Ref-held so an inline onConnected prop doesn't retrigger the mount check
  // every render. Already connected when the row mounts → the set_up step ticks
  // from real state, not only via the popup flow.
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;
  const skip = !!opts?.skipInitialCheck;
  useEffect(() => {
    // A caller already reading the shared status map doesn't need its own copy
    // of the same request — it only needs `connect` and the in-flight state.
    if (skip) return;
    (async () => { if (await check()) onConnectedRef.current(); })();
  }, [check, skip]);

  const connect = async () => {
    setState('connecting'); setError(null);
    try {
      const { data } = await aaiApi.initiateMyConnection(tool);
      if (data.alreadyConnected) { setState('connected'); refresh(tool); onConnectedRef.current(); return; }
      const popup = window.open(data.redirectUrl, '_blank', 'width=560,height=720');
      const finish = async () => {
        window.removeEventListener('message', onMsg);
        clearInterval(poll);
        const ok = await check();
        refresh(tool);
        if (ok) onConnectedRef.current();
      };
      const onMsg = (e: MessageEvent) => {
        // The callback now posts 'aai:connector-connected'; the older type is
        // still accepted so a popup opened before a deploy still resolves.
        const t = e.data?.type;
        if (t === 'aai:connector-connected' || t === 'aai:member-connected') void finish();
      };
      window.addEventListener('message', onMsg);
      const poll = setInterval(() => { if (popup && popup.closed) void finish(); }, 1500);
      setTimeout(() => { window.removeEventListener('message', onMsg); clearInterval(poll); void check(); }, 120000);
    } catch (e: any) {
      setState('idle');
      setError(e?.response?.data?.detail || 'Could not start the connection.');
    }
  };
  return { state, error, connect };
};

// The dive's "set it up" step, made real: connect the tool to YOUR assistant
// right here (member-scoped — one person's grant never shows for anyone else).
// On success the "set_up" checklist step ticks itself from real state.
const ConnectCard: React.FC<{ tool: string; onConnected: () => void }> = ({ tool, onConnected }) => {
  const [skillCount, setSkillCount] = useState<number | null>(null);
  // A dive runs on the participant's OWN account only, so a company-shared
  // connection does not satisfy this step. Knowing one exists lets the card
  // explain why their own is still needed.
  const [orgShared, setOrgShared] = useState(false);
  const { state, error, connect } = useConnectTool(tool, onConnected);

  useEffect(() => {
    aaiApi.getMyConnection(tool)
      .then((r) => setOrgShared(!!r.data.organization_shared))
      .catch(() => setOrgShared(false));
  }, [tool]);

  // GAM-3 payoff, live: once connected, say what it unlocked in the marketplace.
  useEffect(() => {
    if (state !== 'connected' || tool.trim().length < 4) return;
    aaiApi.suggestSkills(tool).then((r) => setSkillCount((r.data.matches || []).length)).catch(() => {});
  }, [state, tool]);

  if (state === 'checking' || state === 'unavailable') return null;
  if (state === 'connected') {
    return (
      <p className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 flex items-center gap-1.5 flex-wrap">
        <CheckCircle2 size={13} className="shrink-0" /> {tool} is connected to your assistant.
        {skillCount != null && skillCount > 0 && (
          <FeatureLink to="/aai/skills" className="font-semibold text-emerald-800 underline">
            {skillCount} ready skill{skillCount === 1 ? '' : 's'} in your marketplace use it →
          </FeatureLink>
        )}
      </p>
    );
  }
  return (
    <div className="rounded-lg border border-line bg-white px-3 py-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[12.5px] text-ink flex-1 min-w-[200px]">
          Connect {tool} so your assistant can actually work in it — that's the "set it up" step.
        </span>
        <button onClick={connect} disabled={state === 'connecting'}
          className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[12px] font-semibold px-3.5 py-1.5 whitespace-nowrap">
          {state === 'connecting' && <Loader2 size={12} className="animate-spin" />} Connect {tool}
        </button>
      </div>
      {orgShared && (
        <p className="text-[11.5px] text-ink-dim mt-1.5 flex items-start gap-1.5">
          <Building2 size={12} className="mt-0.5 shrink-0 text-ink-soft" />
          Your organization shares a {tool} connection for other work, but a dive runs on your
          own account — it's your tools and your call about what your assistant may do in them.
        </p>
      )}
      {error && <p className="text-[11.5px] text-ink-soft mt-1.5">{error}</p>}
    </div>
  );
};

// Module 2 — the tool-dive tracker. One tool a week, the same repeatable format
// each time (set up → real task → validate → keep what worked). Self-contained:
// it owns its own fetch so it can refresh independently of the tools lists.
// The "why this tool" line — where the tool came from and what diving it earns.
const purposeLine = (dive: ToolDive): string | null => {
  const p = dive.purpose;
  if (!p) return null;
  let origin = 'you added it yourself';
  if (p.source === 'org') origin = "it's in your company's IT-cleared stack — your employer's real tool";
  if (p.source === 'personal') origin = "it's in your own daily stack from onboarding";
  let unlocks = '';
  if (p.ready_skills === 1) unlocks = ' · 1 ready-made skill in your marketplace uses it';
  if (p.ready_skills > 1) unlocks = ` · ${p.ready_skills} ready-made skills in your marketplace use it`;
  return `Why this tool: ${origin}${unlocks}. The dive maps what your assistant may do here — and what stays yours.`;
};

const DiveCard: React.FC<{
  dive: ToolDive; open: boolean; busy: boolean;
  onToggleOpen: () => void; onNotes: (notes: string) => void;
  /** Re-read the dive. Steps are derived server-side from real state, so after
   *  connecting a tool or running a turn the card refetches rather than setting
   *  a checkbox it has no business setting. */
  onRefresh: () => void;
  onDone: () => void;
  /** Why "mark done" is refused, when it is. */
  doneError: string | null;
}> = ({ dive, open, busy, onToggleOpen, onNotes, onRefresh, onDone, doneError }) => {
  const done = dive.status === 'done';
  const stepsDone = dive.steps.filter((s) => s.done).length;
  const allDone = dive.steps.every((s) => s.done);
  const [trying, setTrying] = useState(false);
  const st = useToolStatus(dive.tool_name);
  const line = statusLine(st);
  return (
    <div className="bg-white rounded-lg border border-line-soft">
      <button onClick={onToggleOpen} className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left">
        {done
          ? <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
          : <span className="w-[18px] h-[18px] rounded-full border-2 border-line-strong grid place-items-center text-[9px] font-bold text-ink-soft shrink-0">{stepsDone}</span>}
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] font-semibold text-ink truncate">{dive.tool_name}</span>
          <span className="flex items-center gap-2 text-[11.5px]">
            {done
              ? <span className="text-emerald-600">Dive complete</span>
              : <><span className={line.tone}>{line.text}</span>
                  <span className="text-ink-soft">·</span>
                  <span className="text-ink-soft">{stepsDone} of {dive.steps.length} steps</span></>}
          </span>
        </span>
        <StepDots steps={dive.steps} />
        <ChevronDown size={15} className={`text-ink-soft shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-3.5 pb-3.5 pt-1 space-y-2.5 border-t border-gray-50">
          {purposeLine(dive) && (
            <p className="text-[11.5px] text-ink-dim bg-canvas rounded-lg px-2.5 py-2 leading-snug">
              {purposeLine(dive)}
            </p>
          )}
          {/* "Set it up" made concrete: a real connect button, member-scoped.
              Connecting ticks the set_up step from real state. */}
          {/* Connecting ticks step 1 by itself now: the step reads the real
              connection state, so there is nothing to poke. */}
          <ConnectCard tool={dive.tool_name} onConnected={onRefresh} />
          {/* TD-2: the delegation framework IS the dive. */}
          <DelegationWalk tool={dive.tool_name} />
          {/* Step 2 made real: a chat where the assistant does one real task in
              this tool, hands limited to the delegation answers above. */}
          {!done && (
            <button onClick={() => setTrying(true)}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-brand-200 bg-brand-50/50 hover:bg-brand-50 py-2 text-[12.5px] font-semibold text-brand-700">
              <Sparkles size={13} /> Try it with your assistant: one real task, right here
            </button>
          )}
          {trying && (
            <TryToolModal dive={dive} onClose={() => { setTrying(false); onRefresh(); }}
              onActionRan={onRefresh}
              onSaveNote={(t) => {
                let combined = t;
                if (dive.notes) combined = `${dive.notes}\n${t}`;
                onNotes(combined);
              }} />
          )}
          {/* A dive finished under the old checkboxes keeps its completion, but
              its checklist now reads from real activity, so it can show fewer
              ticks than the "complete" beside it. Say that, rather than leaving
              it looking broken. */}
          {done && !allDone && (
            <p className="rounded-lg bg-canvas px-2.5 py-2 text-[11.5px] text-ink-dim">
              You finished this dive before the steps below started reading from real activity, so
              some show unticked. The dive still counts.
            </p>
          )}
          {/* The dive format, one tile per step. These are a READ-OUT, not
              inputs: each one ticks itself from something that happened above
              (the tool connected and every delegation question answered, a turn
              you took with your assistant, an action it actually ran in the
              tool, the note you wrote). They used to be checkboxes, which meant
              a dive could reach 4 of 4 without any of it being true. An unticked
              tile carries the one thing that would tick it. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {dive.steps.map((s, i) => (
              <div key={s.key}
                className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 ${
                  s.done ? 'border-emerald-100 bg-emerald-50/50' : 'border-line-soft bg-canvas/60'
                }`}>
                {s.done
                  ? <Check size={15} className="text-emerald-500 mt-0.5 shrink-0" />
                  : <Circle size={15} className="text-ink-soft mt-0.5 shrink-0" />}
                <span className="min-w-0">
                  <span className="block text-[10px] font-sans font-bold uppercase tracking-wide text-ink-soft">Step {i + 1}</span>
                  <span className={`block text-[12.5px] leading-snug ${s.done ? 'text-ink-soft' : 'text-ink'}`}>{s.label}</span>
                  {!s.done && s.howto && (
                    <span className="mt-0.5 block text-[11px] leading-snug text-ink-soft">{s.howto}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
          <textarea defaultValue={dive.notes || ''} onBlur={(e) => onNotes(e.target.value)} rows={2}
            disabled={done}
            placeholder="What worked? (this becomes your first skill in Module 3)"
            className="w-full rounded-lg border border-line px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:bg-canvas disabled:text-ink-dim disabled:cursor-default" />
          {!done && (
            <div className="flex items-center justify-end gap-3">
              {!allDone && (
                <span className="text-[11.5px] text-ink-soft">
                  Finish the four steps above and this opens up.
                </span>
              )}
              <button onClick={onDone} disabled={busy || !allDone}
                title={allDone ? undefined : 'Every step has to be earned first'}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-40 disabled:hover:bg-brand-500 text-white text-[12px] font-semibold px-3.5 py-1.5 whitespace-nowrap">
                {busy && <Loader2 size={12} className="animate-spin" />} Mark this dive done
              </button>
            </div>
          )}
          {doneError && <p className="text-right text-[11.5px] text-red-600">{doneError}</p>}
        </div>
      )}
    </div>
  );
};

const ToolDiveTracker: React.FC = () => {
  const [data, setData] = useState<DiveTrackerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  // A generic queue entry ("Email") expands into the real connectors behind it
  // rather than starting a dive that can never connect.
  const [picking, setPicking] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { const { data } = await aaiApi.getToolDives(); setData(data); } catch { /* tracker is best-effort */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const start = async (tool: string) => {
    const t = tool.trim();
    if (!t) return;
    setBusy(true);
    setStartError(null);
    try { const { data: dive } = await aaiApi.startToolDive(t); await load(); setOpen(dive.id); }
    catch (e: any) { setStartError(e?.response?.data?.detail || 'Could not start that dive.'); }
    finally { setBusy(false); }
  };
  // No step writer any more. The four steps are derived from real state
  // server-side, so there is nothing for the browser to set.
  const [doneError, setDoneError] = useState<string | null>(null);
  const notes = async (dive: ToolDive, value: string) => {
    if (value === (dive.notes || '')) return;
    try { await aaiApi.updateToolDive(dive.id, { notes: value }); await load(); } catch { /* best-effort */ }
  };
  const done = async (dive: ToolDive) => {
    setBusy(true);
    setDoneError(null);
    try { await aaiApi.updateToolDive(dive.id, { status: 'done' }); await load(); }
    catch (e: any) { setDoneError(e?.response?.data?.detail || 'Could not finish that dive yet.'); }
    finally { setBusy(false); }
  };
  if (loading || !data) return null;
  const { dives, suggestions, progress } = data;
  const queue = data.queue ?? suggestions.map((t) => ({ tool: t, from: 'personal' as const, options: [] as string[] }));
  const active = dives.find((d) => d.status !== 'done') || null;
  // One tool a week: while a dive is open, that IS this week's tool. The
  // backend refuses a second one, so the queue says why instead of offering
  // buttons that come back with an error.
  const weekTaken = !!active;
  // The active dive is open unless the person collapsed it ('' = collapsed by
  // hand) or opened another. The whole point of the section used to be
  // invisible behind a closed accordion.
  const resolveOpenId = (): string | null => {
    if (open === '') return null;
    if (open) return open;
    if (active) return active.id;
    return null;
  };
  const openId = resolveOpenId();

  return (
    <Zone
      step={2}
      label="This week's dive"
      blurb="One tool at a time: connect it, set what it may do, use it on one real task, keep what worked."
      aside={
        <>
          <div className="text-[15px] font-bold text-ink">{progress.done} of {progress.target}</div>
          <div className="text-[10.5px] font-sans uppercase tracking-wide text-ink-soft">dives done</div>
        </>
      }
    >
      {dives.length === 0 ? (
        <p className="text-[13px] text-ink-dim bg-white rounded-lg border border-line-soft px-3.5 py-3">
          {queue.length > 0
            ? 'Nothing dived yet. Start with one of the tools below and take it deep.'
            : 'No tools assigned to dive yet. Your organization sets which tools are in the program.'}
        </p>
      ) : (
        <div className="space-y-2">
          {dives.map((d) => (
            <DiveCard key={d.id} dive={d} open={openId === d.id} busy={busy}
              onToggleOpen={() => setOpen(openId === d.id ? '' : d.id)}
              onNotes={(v) => notes(d, v)} onRefresh={() => { void load(); }}
              onDone={() => done(d)} doneError={doneError} />
          ))}
        </div>
      )}

      {/* TD-5: what to dive next — the employer's IT-cleared tools first, then
          the one they named at onboarding. The participant does not add tools
          here: which tools are in the program is the organization's call. A
          generic answer ("Email") asks which product it means. */}
      {queue.length > 0 && (
      <div className="mt-3 pt-3 border-t border-line-soft/80">
        <p className="text-[11px] font-sans font-semibold uppercase tracking-wide text-ink-soft mb-2">Dive next</p>
        {weekTaken && (
          <p className="text-[12px] text-ink-dim">
            {active!.tool_name} is this week's tool. Finish that dive and the next one opens up:{' '}
            <span className="text-ink-soft">{queue.map((q) => q.tool).join(', ')}</span>.
          </p>
        )}
        {!weekTaken && (
        <div className="flex flex-wrap items-center gap-1.5">
          {queue.map((q) => (
            (q.options && q.options.length > 0 && picking === q.tool) ? (
              <span key={q.tool} className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 border border-brand-200 pl-2.5 pr-1 py-1">
                <span className="text-[11.5px] text-brand-700">Which {q.tool.toLowerCase()}?</span>
                {q.options.map((o) => (
                  <button key={o} onClick={() => { setPicking(null); void start(o); }} disabled={busy}
                    className="rounded-full bg-white border border-brand-200 hover:border-brand-400 px-2 py-0.5 text-[11.5px] font-semibold text-brand-700 disabled:opacity-50">
                    {o}
                  </button>
                ))}
                <button onClick={() => setPicking(null)} className="text-ink-soft hover:text-ink-muted px-1 text-[13px]">×</button>
              </span>
            ) : (
              <button key={q.tool}
                onClick={() => { if (q.options && q.options.length > 0) setPicking(q.tool); else void start(q.tool); }}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-full border border-line hover:border-brand-300 bg-white px-2.5 py-1 text-[12px] text-ink disabled:opacity-50">
                <Plus size={12} /> {q.tool}
                {q.from === 'org' && <span className="text-[9.5px] font-bold uppercase tracking-wide text-brand-500">org</span>}
              </button>
            )
          ))}
        </div>
        )}
        {startError && <p className="text-[11.5px] text-red-500 mt-1.5">{startError}</p>}
      </div>
      )}
    </Zone>
  );
};

// Module 4 — vet a workflow skill: log runs (worked / partly / didn't work), revise.
// "Doing it once doesn't count" — a skill becomes successful once it has WORKED 3
// times. Successful skills can be shared, but only after Module 5 unlocks; an
// unsuccessful skill is never shareable (participant privacy). Revising forks a new
// instance so the history is kept.
const OUTCOMES: { v: string; label: string; cls: string }[] = [
  { v: 'worked', label: 'Worked', cls: 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' },
  { v: 'partial', label: 'Partly', cls: 'border-amber-300 text-amber-700 hover:bg-amber-50' },
  { v: 'failed', label: "Didn't work", cls: 'border-red-300 text-red-700 hover:bg-red-50' },
];

const OUTCOME_DOT: Record<string, string> = { worked: 'bg-emerald-500', partial: 'bg-amber-500', failed: 'bg-red-500' };

// What a workflow skill's row says about itself, in the words the section uses:
// how many clean runs it has, and what happens next.
const vettingLine = (v: VettingData['vetting'] | undefined, grad: Graduation | null): React.ReactNode => {
  if (!v) return <span className="text-ink-soft">checking…</span>;
  if (grad?.status === 'approved') {
    return <span className="text-emerald-600">Proven and shared with your {grad.to_scope === 'team' ? 'team' : 'company'}</span>;
  }
  if (grad?.status === 'proposed') return <span className="text-brand-600">Proven · waiting on your champion</span>;
  if (v.vetted) return <span className="text-emerald-600">Proven — it worked {v.worked} times. Sharing is your call.</span>;
  const left = v.target.worked - v.worked;
  if (v.worked === 0) return <span className="text-ink-soft">Not run yet · use it on real work {v.target.worked} times to prove it</span>;
  return <span className="text-amber-600">Worked {v.worked} of {v.target.worked} times · {left} more to prove it</span>;
};

const WorkflowSkillCard: React.FC<{ skill: PersonalSkill; grad: Graduation | null; sharingUnlocked: boolean; onRevise: () => void; onShared: () => void; onDeleted: () => void }> = ({ skill, grad, sharingUnlocked, onRevise, onShared, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<VettingData | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const reuse = reusableText(skill);

  const load = useCallback(async () => {
    try { const { data } = await aaiApi.getSkillVetting(skill.id); setData(data); } catch { /* best-effort */ }
  }, [skill.id]);
  // Loaded on mount, not on open: "2 of 3 runs" IS the status of this section,
  // and hiding it behind an accordion made every skill row look identical.
  useEffect(() => { void load(); }, [load]);
  const toggle = () => setOpen((v) => !v);
  const copy = async () => {
    try { await navigator.clipboard.writeText(reuse); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { /* clipboard blocked — the text is still visible to select manually */ }
  };
  const logRun = async (outcome: string) => {
    setBusy(true);
    setRunError(null);
    try { await aaiApi.logSkillRun(skill.id, outcome, note); setNote(''); await load(); }
    catch (e: any) { setRunError(e?.response?.data?.detail || 'Could not log that run.'); }
    finally { setBusy(false); }
  };
  const share = async () => {
    setBusy(true);
    try { await aaiApi.proposeGraduation(skill.id); onShared(); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${skill.name}"? Its runs and history go too. This can't be undone.`)) return;
    setDeleting(true);
    try { await aaiApi.deleteMySkill(skill.id); onDeleted(); }
    catch { setDeleting(false); /* leave the card so the user can retry */ }
  };

  const v = data?.vetting;
  // An outcome report is spent against a real run. Undefined means an older
  // backend that does not report it: fail OPEN there rather than blocking a
  // person out of a loop that used to work.
  const canReport = v ? (v.unreported === undefined || v.unreported > 0) : false;
  return (
    <div className="bg-white rounded-lg border border-line-soft">
      <button onClick={toggle} className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left">
        {v?.vetted
          ? <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
          : <FlaskConical size={18} className="text-ink-soft shrink-0" />}
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] font-semibold text-ink truncate">{skill.name}</span>
          <span className="block text-[11.5px]">{vettingLine(v, grad)}</span>
        </span>
        {v && (
          <span className="hidden sm:inline-flex items-center gap-1 shrink-0">
            {Array.from({ length: v.target.worked }).map((_, i) => (
              <span key={i} className={`h-1.5 w-1.5 rounded-full ${i < v.worked ? 'bg-emerald-500' : 'bg-line'}`} />
            ))}
          </span>
        )}
        <ChevronDown size={15} className={`text-ink-soft shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-3.5 pb-3.5 pt-1 border-t border-gray-50">
          {!data ? <div className="py-3"><Loader2 size={16} className="animate-spin text-ink-soft" /></div> : (
            <>
              {reuse && (
                <div className="mb-3">
                  <MonoLabel className="mb-1.5">Your skill — run it</MonoLabel>
                  <div className="rounded-lg border border-line bg-canvas px-3 py-2.5 whitespace-pre-wrap text-[12.5px] text-ink leading-relaxed max-h-56 overflow-y-auto">
                    {reuse}
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <button onClick={() => setRunning(true)}
                      className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-[12px] font-semibold px-3.5 py-1.5 whitespace-nowrap">
                      <Play size={13} /> Run this skill
                    </button>
                    <button onClick={copy}
                      className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white hover:border-brand-300 text-ink-muted text-[12px] font-semibold px-3.5 py-1.5 whitespace-nowrap">
                      {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy for external AI</>}
                    </button>
                  </div>
                </div>
              )}
              {running && (
                <RunSkillModal skill={skill} onClose={() => setRunning(false)}
                  onLogRun={async (outcome, runNote) => {
                    await aaiApi.logSkillRun(skill.id, outcome, runNote);
                    await load();
                  }} />
              )}
              <p className="text-[12px] text-ink-dim mb-2">
                Use it on real work and log how it went. It becomes successful once it’s <b>worked {v!.target.worked} times</b> — {v!.worked} so far.
                {v!.vetted ? ' You’re there — nice.' : ' Doing it once doesn’t count.'}
              </p>
              <MonoLabel className="mb-1.5">Log a run</MonoLabel>
              {/* An outcome has to be spent against a real run. Saying "it
                  worked" used to be a click, so three clicks could graduate a
                  skill nobody had used. Run it above, then come back. */}
              {!canReport && (
                <p className="mb-2 rounded-lg bg-amber-50 px-2.5 py-2 text-[11.5px] text-amber-800">
                  Run the skill first, then tell us how it went. Use <b>Run this skill</b> above, or
                  copy it into the AI you use, and this opens up.
                </p>
              )}
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                disabled={!canReport}
                placeholder="What happened? What to fix next?"
                className="w-full rounded-lg border border-line px-2.5 py-1.5 text-[12.5px] mb-2 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:bg-canvas disabled:cursor-not-allowed" />
              {runError && <p className="mb-2 text-[11.5px] text-red-600">{runError}</p>}
              <div className="flex flex-wrap gap-1.5">
                {OUTCOMES.map((o) => (
                  <button key={o.v} onClick={() => logRun(o.v)} disabled={busy || !canReport}
                    title={canReport ? undefined : 'Run the skill first'}
                    className={`inline-flex items-center gap-1 rounded-full border bg-white px-2.5 py-1 text-[12px] font-medium disabled:opacity-40 disabled:cursor-not-allowed ${o.cls} whitespace-nowrap`}>
                    {busy ? <Loader2 size={12} className="animate-spin" /> : null} {o.label}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-1.5">
                  <button onClick={onRevise}
                    className="inline-flex items-center gap-1 rounded-full border border-line bg-white px-2.5 py-1 text-[12px] font-medium text-ink-muted hover:border-brand-300 whitespace-nowrap">
                    <RotateCcw size={12} /> Revise
                  </button>
                  <button onClick={remove} disabled={deleting}
                    className="inline-flex items-center gap-1 rounded-full border border-line bg-white px-2.5 py-1 text-[12px] font-medium text-ink-muted hover:border-red-300 hover:text-red-600 disabled:opacity-50 whitespace-nowrap">
                    {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete
                  </button>
                </div>
              </div>

              {data.runs.length > 0 && (
                <div className="mt-3">
                  <MonoLabel className="mb-1.5">Runs</MonoLabel>
                  <div className="space-y-1">
                    {data.runs.map((r) => (
                      <div key={r.id} className="flex items-start gap-2 text-[12px]">
                        <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${OUTCOME_DOT[r.outcome] || 'bg-gray-300'}`} />
                        <span className="text-ink-muted">
                          <span className="text-ink-soft">v{r.version_no} · </span>{r.note || r.outcome}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data.versions.length > 0 && (
                <p className="mt-3 text-[11.5px] text-ink-soft">
                  {data.versions.length} earlier edit{data.versions.length === 1 ? '' : 's'} kept in this skill’s history. Revising now forks a new, separate skill.
                </p>
              )}

              {/* Share (graduation) — successful skills only, and only once Module 5
                  is unlocked. An unsuccessful skill is never shareable (privacy). */}
              <div className="mt-3 pt-3 border-t border-gray-50">
                {grad ? (
                  <span className={`inline-flex items-center gap-1.5 text-[12px] font-semibold ${grad.status === 'approved' ? 'text-emerald-600' : grad.status === 'declined' ? 'text-ink-dim' : 'text-brand-600'}`}>
                    {grad.status === 'approved'
                      ? <><Share2 size={13} /> Shared with your {grad.to_scope === 'team' ? 'team' : 'company'} ✓</>
                      : grad.status === 'declined'
                        ? <>Not approved — refine and reshare</>
                        : <><Clock size={13} /> Shared — pending champion review</>}
                  </span>
                ) : !data.vetting.vetted ? (
                  <span className="text-[11.5px] text-ink-soft">Only successful skills can be shared. Make this one work 3 times first — nothing unsuccessful is ever shared.</span>
                ) : sharingUnlocked ? (
                  <button onClick={share} disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[12px] font-semibold px-3.5 py-1.5 whitespace-nowrap">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={13} />} Share with my team
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-600">
                    <Check size={13} /> Successful — ready to share.
                    <span className="inline-flex items-center gap-1 font-medium text-ink-soft"><Lock size={11} /> Sharing opens in Module 5 (Team Adoption).</span>
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const WorkflowVetting: React.FC = () => {
  const { go } = useFeatureNav();
  const [skills, setSkills] = useState<PersonalSkill[] | null>(null);
  const [grads, setGrads] = useState<Record<string, Graduation>>({});
  const [sharingUnlocked, setSharingUnlocked] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const loadGrads = useCallback(async () => {
    try {
      const { data } = await aaiApi.getMyGraduations();
      const map: Record<string, Graduation> = {};
      (data.graduations || []).forEach((g) => { if (!map[g.skill_id]) map[g.skill_id] = g; }); // ordered newest-first
      setGrads(map);
      setSharingUnlocked(!!data.sharing_unlocked);
    } catch { /* best-effort */ }
  }, []);
  useEffect(() => {
    aaiApi.getMyWorkflowSkills()
      .then((r) => setSkills(r.data.skills || []))
      .catch(() => setSkills([]));
    void loadGrads();
  }, [loadGrads]);

  if (!skills || skills.length === 0) return null;
  const visible = showAll ? skills : skills.slice(0, 3);
  const hidden = skills.length - visible.length;
  return (
    <div>
      <SkillGroupLabel
        label="Workflow skills"
        blurb="Use each one on real work and log how it went. It counts as proven once it has worked three times, and only then is sharing yours to offer."
      />
      <div className="space-y-2">
        {visible.map((s) => (
          <WorkflowSkillCard key={s.id} skill={s} grad={grads[s.id] || null} sharingUnlocked={sharingUnlocked}
            onRevise={() => go(`/skill-builder/workflow?revise=${encodeURIComponent(s.id)}`)} onShared={loadGrads}
            onDeleted={() => setSkills((prev) => (prev || []).filter((x) => x.id !== s.id))} />
        ))}
      </div>
      {hidden > 0 && (
        <button onClick={() => setShowAll(true)}
          className="mt-2 text-[12px] font-semibold text-brand-600 hover:text-brand-700">
          Show {hidden} more workflow skill{hidden === 1 ? '' : 's'}
        </button>
      )}
    </div>
  );
};

// A group heading inside the skills zone — the two kinds of skill are different
// enough to label, not different enough to justify two whole cards.
const SkillGroupLabel: React.FC<{ label: string; blurb?: string; aside?: React.ReactNode }> = ({ label, blurb, aside }) => (
  <div className="flex items-start justify-between gap-3 mb-2">
    <div className="min-w-0">
      <div className="text-[12.5px] font-bold text-ink">{label}</div>
      {blurb && <p className="text-[11.5px] text-ink-dim leading-snug mt-0.5">{blurb}</p>}
    </div>
    {aside && <div className="shrink-0">{aside}</div>}
  </div>
);

// The reusable text a skill puts on your clipboard — a context "about me" block,
// or a workflow's steps + example. This is what "Reusable" actually delivers.
const reusableText = (s: PersonalSkill): string => {
  if (s.kind !== 'workflow') return s.example_input || s.problem || '';
  return [
    s.problem?.trim(),
    (s.process_steps || []).length ? `Steps:\n${s.process_steps.map((x, i) => `${i + 1}. ${x}`).join('\n')}` : '',
    s.example_input?.trim() && `Example input:\n${s.example_input.trim()}`,
    s.example_output?.trim() && `Good output:\n${s.example_output.trim()}`,
  ].filter(Boolean).join('\n\n');
};

// Module 3 — "Skills you've built". Each row is genuinely reusable: expand to
// read the skill and copy its block to paste before your next AI task, or delete
// it if you no longer need it.
const BuiltSkillRow: React.FC<{ skill: PersonalSkill; onDeleted: () => void }> = ({ skill, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // F1: a skill you keep is a thing you will do again. This is where you say
  // "every weekday at nine" instead of remembering to run it yourself.
  const [scheduling, setScheduling] = useState(false);
  const text = reusableText(skill);

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { /* clipboard blocked — the text is still visible to select manually */ }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${skill.name}"? This can't be undone.`)) return;
    setDeleting(true);
    try { await aaiApi.deleteMySkill(skill.id); onDeleted(); }
    catch { setDeleting(false); /* leave the row so the user can retry */ }
  };

  return (
    <div className="bg-white rounded-lg border border-line-soft">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left">
        <span className="w-7 h-7 rounded-md bg-accent-100 text-amber-700 grid place-items-center shrink-0"><Sparkles size={14} /></span>
        <span className="text-[13px] font-medium flex-1 min-w-0 truncate">{skill.name}</span>
        <span className="text-[11px] font-semibold text-emerald-600 shrink-0">Reusable</span>
        <ChevronDown size={15} className={`text-ink-soft shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-3.5 pb-3.5 pt-1 border-t border-gray-50">
          {skill.problem && skill.kind !== 'workflow' && <p className="text-[12px] text-ink-dim mb-2">{skill.problem}</p>}
          <div className="rounded-lg border border-line bg-canvas px-3 py-2.5 whitespace-pre-wrap text-[12.5px] text-ink leading-relaxed max-h-64 overflow-y-auto">
            {text || 'This skill has no reusable content yet — refine it to add some.'}
          </div>
          <div className="flex items-center gap-2 mt-2.5">
            <button onClick={copy} disabled={!text}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-40 text-white text-[12px] font-semibold px-3.5 py-1.5 whitespace-nowrap">
              {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy to reuse</>}
            </button>
            <button onClick={() => setScheduling(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white hover:border-brand-300 hover:text-brand-600 text-ink-muted text-[12px] font-semibold px-3.5 py-1.5 whitespace-nowrap">
              <CalendarClock size={12} /> Run on a schedule
            </button>
            <button onClick={remove} disabled={deleting}
              className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white hover:border-red-300 hover:text-red-600 disabled:opacity-50 text-ink-muted text-[12px] font-semibold px-3.5 py-1.5 whitespace-nowrap">
              {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete skill
            </button>
          </div>
        </div>
      )}
      {scheduling && (
        <ScheduleSkillDialog
          skill={{ id: skill.id, name: skill.name }}
          onClose={() => setScheduling(false)}
        />
      )}
    </div>
  );
};

const BuiltSkillsCard: React.FC<{ fallback: { id?: string; name: string }[] }> = ({ fallback }) => {
  const [skills, setSkills] = useState<PersonalSkill[] | null>(null);
  // Module 3 shows context skills only — workflow (vetted) skills live in the
  // Module 4 "Vet your workflow skills" section, kept separate for training.
  useEffect(() => {
    aaiApi.getMySkills()
      .then((r) => setSkills((r.data.skills || []).filter((s) => s.kind !== 'workflow')))
      .catch(() => setSkills(null));
  }, []);

  const empty = (skills ? skills.length === 0 : fallback.length === 0);
  return (
    <div>
      <SkillGroupLabel
        label="Context skills"
        blurb="The block about you that you paste before an AI task, so you never re-explain yourself."
        aside={
          <FeatureLink to="/skill-builder"
            className="inline-flex items-center gap-1 rounded-full border border-line bg-white hover:border-brand-300 px-2.5 py-1 text-[11.5px] font-semibold text-brand-600">
            <Plus size={11} /> Build one
          </FeatureLink>
        }
      />
      {empty ? (
        <p className="text-[12.5px] text-ink-dim bg-white rounded-lg border border-line-soft px-3.5 py-3">
          None yet. The skill builder turns your onboarding answers into one, and it lands here ready to copy.
        </p>
      ) : skills ? (
        <div className="space-y-2">
          {skills.map((s) => (
            <BuiltSkillRow key={s.id} skill={s}
              onDeleted={() => setSkills((prev) => (prev || []).filter((x) => x.id !== s.id))} />
          ))}
        </div>
      ) : (
        // Full skill content couldn't load — show names so the section still reads.
        <div className="space-y-2">
          {fallback.map((s, i) => (
            <div key={s.id || i} className="flex items-center gap-3 bg-white rounded-lg px-3.5 py-2.5 border border-line-soft">
              <span className="w-7 h-7 rounded-md bg-accent-100 text-amber-700 grid place-items-center shrink-0"><Sparkles size={14} /></span>
              <span className="text-[13px] font-medium flex-1 min-w-0 truncate">{s.name}</span>
              <span className="text-[11px] font-semibold text-emerald-600">Reusable</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// The delegation framework (design's "Your delegation matrix") — four ways to
// delegate a task. Static coaching framework; skills plot into it once per-skill
// trust posture is a live setting (posture capture is on the roadmap).
const DELEGATION_MATRIX = [
  { label: 'Automate now', note: 'high trust · high value', cls: 'bg-emerald-50 text-emerald-700' },
  { label: 'Trust gap', note: 'high value · building trust', cls: 'bg-amber-50 text-amber-700' },
  { label: 'Human edge', note: 'your judgment · your call', cls: 'bg-brand-50 text-brand-700' },
  { label: 'Park it', note: 'low value for now', cls: 'bg-canvas-inset text-ink-dim' },
];

const MyToolsPage: React.FC = () => {
  const { data, loading, error } = useAsync<MyToolsData>(() => aaiApi.getMyTools(), 'Could not load your tools.');
  if (loading) return <Loading label="Loading your tools…" />;
  if (error || !data) return <ErrorNote message={error || 'Something went wrong.'} />;

  return (
    // Connection + delegation state is read once per tool and shared by every
    // zone, so a tile and a dive row can never disagree about the same tool.
    <ToolStatusProvider>
      <div className="max-w-4xl mx-auto">
        <PageHeader
          title="My tools & skills"
          subtitle={data.onboarded
            ? 'Wire up the tools you already work in, take one deep at a time, and keep what worked as a skill you can reuse.'
            : 'Complete onboarding to map your tools and skills.'}
        />

        <div className="space-y-4">
          {/* 1 — the tools themselves, with the only status that matters on
              their face: wired to my assistant, and what it may do. */}
          <Zone
            step={1}
            label="Your tools"
            blurb="The tools you told us you work in. Connecting one lets your assistant actually do the work in it, not just talk about it."
          >
            <ToolTiles tools={data.tools} />
            {data.ai_tools.length > 0 && (
              <div className="mt-3 pt-3 border-t border-line-soft/80">
                <p className="text-[11px] font-sans font-semibold uppercase tracking-wide text-ink-soft mb-1.5">AI you have tried</p>
                <div className="flex flex-wrap gap-1.5">
                  {data.ai_tools.map((t, i) => (
                    <span key={t.value || i} className="rounded-full bg-white border border-line-soft px-2.5 py-1 text-[12px] text-ink-muted">{t.label}</span>
                  ))}
                </div>
              </div>
            )}
          </Zone>

          {/* 2 — the weekly dive: the active one open, the rest legible. */}
          <ToolDiveTracker />

          {/* 3 — what the dives leave behind. Both kinds of skill in one place,
              each showing how close it is to being proven. */}
          <Zone
            step={3}
            label="Your skills"
            blurb="What the dives leave behind: reusable skills built from your own work."
            aside={
              <FeatureLink to="/skill-builder/workflow"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-[11.5px] font-semibold px-3 py-1.5">
                <Plus size={12} /> New workflow skill
              </FeatureLink>
            }
          >
            <div className="space-y-4">
              <WorkflowVetting />
              <BuiltSkillsCard fallback={data.skills} />
            </div>
          </Zone>

          {/* Delivered builds — only when a request has actually shipped. */}
          {data.built_tools.length > 0 && (
            <Card>
              <MonoLabel className="mb-3">Built for you</MonoLabel>
              <div className="space-y-2">
                {data.built_tools.map((t) => (
                  <FeatureLink key={t.id} to="/aai/skills"
                    className="flex items-center gap-3 bg-white rounded-lg px-3.5 py-2.5 border border-line-soft hover:border-brand-200">
                    <span className="w-7 h-7 rounded-md bg-brand-50 text-brand-600 grid place-items-center shrink-0"><Wrench size={14} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-medium truncate">{t.name}</span>
                      <span className="block text-[11.5px] text-ink-soft truncate">From your request: {t.request_title}</span>
                    </span>
                    <span className="text-[11px] font-semibold text-emerald-600 shrink-0">Delivered</span>
                    <ArrowRight size={14} className="text-ink-soft shrink-0" />
                  </FeatureLink>
                ))}
              </div>
            </Card>
          )}

          {/* Coaching framework, not live data — said plainly, and last. Reads
              like every other section: no collapse of its own. */}
          <Card>
            <MonoLabel className="mb-1">Your delegation matrix</MonoLabel>
            <p className="text-[12.5px] text-ink-dim mb-3">
              A way to think about what to hand over. This is the framework, not a read of your own skills.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {DELEGATION_MATRIX.map((q) => (
                <div key={q.label} className="bg-white rounded-[11px] p-4">
                  <span className={`inline-block text-[12px] font-bold rounded-full px-3 py-0.5 ${q.cls}`}>{q.label}</span>
                  <div className="text-[11.5px] text-ink-dim mt-2.5">{q.note}</div>
                </div>
              ))}
            </div>
          </Card>

          {data.learning_pref && (
            <Card>
              <MonoLabel className="mb-1.5">How you like to learn</MonoLabel>
              <div className="text-[14px] font-medium text-ink">{data.learning_pref}</div>
            </Card>
          )}
        </div>
      </div>
    </ToolStatusProvider>
  );
};

export default MyToolsPage;
